import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getOrganizationIds,
	getOrganizationIdsFromReportFetchRequest,
	getReport,
	getReportCustomFieldsResources,
	parseOrganizationIds,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import {
	getCommonBarcodeFilter,
	getCommonBarcodeGroup,
	getCommonPaymentStatusFilter,
	getCommonPaymentStatusGroup,
	getCommonPaymentStatusGroupInfo,
} from "../../../../../../../../utils/reports";
import ReportCategoriesProductUtils from "../../../../../../../../utils/reports/ReportCategoriesProductUtils";
import { ReportSaleUtils } from "../../../../../../../../utils/reports/ReportSaleUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface Props {
	resource?: Record<string, any>;
	setOrganizationsIds: (organizationIds: number[]) => void;
	user: UserState;
}

const listName = "CATEGORY_REPORT_GROUP";
const resourceType = "CATEGORY_REPORT_GROUP";

const Report = ({ resource, setOrganizationsIds, user }: Props) => {
	const { t } = useTranslation();
	const [filtersParams, setFilterParams] = useState("");
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	let config = {
		reportConfigId: "categories",
		type: "order_item",
		chartTitle: t("modules.category_report.field.report.title"),
		aggregatePrefix: "sales",
		chartAggregate: "total_money",
		chartType: getChartType(),
		selectedColumns: ReportCategoriesProductUtils.getSelectedColumns("categories"),
		groups: [
			...ReportShareUtils.getGroupReportCommonGroups(t),
			...ReportCategoriesProductUtils.getGroups(t),
			...ReportShareUtils.getGroups(t),
			getCommonBarcodeGroup(t),
			getCommonPaymentStatusGroup(t),
		],
		groupsInfo: { ...ReportShareUtils.getGroupsInfo(t), ...getCommonPaymentStatusGroupInfo(t) },
		selectedGroups: ["PRODUCT_CATEGORY"],
		showPercent: true,
		columns: ReportCategoriesProductUtils.getColumns(t),
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			},
			{
				id: "organization_ids",
				filterType: "list",
				condition: "e",
				value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
				visible: true,
			},
		],
		filters: [
			...ReportCategoriesProductUtils.getFilters(t, filtersParams),
			...ReportShareUtils.getFilters(t, user),
			getCommonBarcodeFilter(t, filtersParams),
			getCommonPaymentStatusFilter(t),
			ReportSaleUtils.getFilters(t).find((filter) => filter.id === "fiscalization_status"),
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: ReportCategoriesProductUtils.getSelectedColumns("categories"),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
					{
						filterId: "organization_ids",
						filterType: "list",
						condition: "e",
						value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			setOrganizationsIds(getOrganizationIdsFromReportFetchRequest(params.f));
			setFilterParams(params.f);
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					config.type,
					sourceToken,
					undefined,
					"ORDERS"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"PRODUCT_CATEGORY",
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					config.type,
					sourceToken,
					5,
					"ORDERS"
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"PRODUCT_CATEGORY",
				getOrganizationIdsFromReportFetchRequest(params.f),
				config.filters,
				config.type,
				sourceToken,
				5,
				"ORDERS"
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.category_report.field.export_config.title"),
			filename: t("modules.category_report.field.export_config_filename.title"),
			pdfOrientation: "ASK",
		},
		withCompareDateRange: true,
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ITEM", "ORDER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const UserReportsCategoriesPage = (): JSX.Element => {
	const user = useSelector(selectUser);
	const [organizationsIds, setOrganizationsIds] = useState<number[]>(() =>
		parseOrganizationIds(user.organizations ?? [])
	);
	const organizationIdsAsString = organizationsIds.join();
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ITEM",
			name: t(`enums.custom_fields.resources.ITEM`),
		},
		{
			type: "ORDER",
			name: t(`enums.custom_fields.resources.ORDER`),
		},
	];

	useEffect(() => {
		const params: Record<string, any> = {};
		if (organizationsIds?.length > 0) {
			params.organization_id = organizationIdsAsString;
		}

		setResource(wrapPromise(segmentContext.get(listName, resourceType, customFieldResourceTypes, params)));
	}, [organizationIdsAsString]);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} setOrganizationsIds={setOrganizationsIds} user={user} />
		</Suspense>
	);
};

export default UserReportsCategoriesPage;
